"use client"

import { useState, useEffect, useMemo } from "react"
import { Bell } from "lucide-react"
import { useNavigate } from "react-router"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import { StudentsFilters } from "../components/ui/school/students-management/students-filters"
import { StudentsTable } from "../components/ui/school/students-management/students-table"
import { PaginationStats } from "../components/ui/school/students-management/pagination-stats"
import { listStudents } from "../hooks/api/schoolApi"

export default function StudentsManagementPage() {
  const navigate = useNavigate()
  const [students, setStudents] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ searchQuery: "", grade: "", careerPath: "" })
  const [currentPage, setCurrentPage] = useState(1)
  const resultsPerPage = 8

  useEffect(() => {
    setLoading(true)
    listStudents({
      page: currentPage,
      limit: resultsPerPage,
      search: filters.searchQuery || undefined,
      classLevel: filters.grade || undefined,
    })
      .then((res) => {
        setStudents(
          res.students.map((s) => ({
            id: s._id,
            name: `${s.firstName} ${s.lastName}`,
            grade: s.classLevel,
            classSection: s.classLevel,
            progressPercentage: s.performanceMetrics?.academicScore ?? 0,
            careerPath: s.careerInterests?.[0] ?? "—",
            status: s.status,
          }))
        )
        setTotal(res.total)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [currentPage, filters.searchQuery, filters.grade])

  const totalPages = Math.ceil(total / resultsPerPage)

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }

  const handleViewStudent = (id) => {
    navigate(`/students-management/${id}`)
  }

  return (
    <SchoolLayout>
      <div className="p-4 md:p-8">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Students Management</h1>
            <p className="text-gray-500 mt-1">View and manage all student records</p>
          </div>
          <button className="relative p-3 rounded-full hover:bg-white transition border border-gray-200">
            <Bell className="text-gray-600 w-6 h-6" />
            <span className="absolute top-2 right-2 block h-2 w-2 rounded-full bg-red-500"></span>
          </button>
        </header>

        <StudentsFilters onFilterChange={handleFilterChange} />

        {loading ? (
          <div className="flex justify-center items-center py-20 text-gray-400">Loading students…</div>
        ) : (
          <StudentsTable students={students} onViewStudent={handleViewStudent} />
        )}

        {!loading && total > 0 && (
          <PaginationStats
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={total}
            resultsPerPage={resultsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </SchoolLayout>
  )
}
